import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{ route: '/legal-notice/disclaimer', priority: 0.7 },
	],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Disclaimer Policy  | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Disclaimer Policy for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

export default function DisclaimerPolicyRoute() {
	return (
		<main className="mx-auto max-w-4xl px-4 py-8">
			<h1 className="mb-6 text-3xl font-bold">DISCLAIMER</h1>
			<p className="mb-4">
				<strong className="mr-2">Last updated</strong>
				<strong>October 6, 2025</strong>
			</p>
			<p className="mb-4">
				The information provided by Knight Rider Consulting, Inc. ("we," "us,"
				or "our") on
				<a href="/" className="mx-1 text-blue-600 hover:underline">
					https://www.trailviewr.com
				</a>{' '}
				(the "Site") is for general informational purposes only. All information
				on the Site is provided in good faith, however we make no representation
				or warranty of any kind, express or implied, regarding the accuracy,
				adequacy, validity, reliability, availability, or completeness of any
				information on the Site. UNDER NO CIRCUMSTANCE SHALL WE HAVE ANY
				LIABILITY TO YOU FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT
				OF THE USE OF THE SITE OR RELIANCE ON ANY INFORMATION PROVIDED ON THE
				SITE. YOUR USE OF THE SITE AND YOUR RELIANCE ON ANY INFORMATION ON THE
				SITE IS SOLELY AT YOUR OWN RISK.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">
				EXTERNAL LINKS DISCLAIMER
			</h2>
			<p className="mb-4">
				The Site may contain (or you may be sent through the Site) links to
				other websites or content belonging to or originating from third parties
				or links to websites and features in banners or other advertising. Such
				external links are not investigated, monitored, or checked for accuracy,
				adequacy, validity, reliability, availability, or completeness by us. WE
				DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR THE
				ACCURACY OR RELIABILITY OF ANY INFORMATION OFFERED BY THIRD-PARTY
				WEBSITES LINKED THROUGH THE SITE OR ANY WEBSITE OR FEATURE LINKED IN ANY
				BANNER OR OTHER ADVERTISING. WE WILL NOT BE A PARTY TO OR IN ANY WAY BE
				RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY
				PROVIDERS OF PRODUCTS OR SERVICES.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">PROFESSIONAL DISCLAIMER</h2>
			<p className="mb-4">
				The Site cannot and does not contain medical, fitness, or other advice.
				The medical, fitness, and other information is provided for general
				informational and educational purposes only and is not a substitute for
				professional advice. Accordingly, before taking any actions based upon
				such information, we encourage you to consult with the appropriate
				professionals. We do not provide any kind of medical, fitness, or other
				advice. THE USE OR RELIANCE OF ANY INFORMATION CONTAINED ON THE SITE IS
				SOLELY AT YOUR OWN RISK.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">AFFILIATES DISCLAIMER</h2>
			<p className="mb-4">
				The Site may contain links to affiliate websites, and we receive an
				affiliate commission for any purchases made by you on the affiliate
				website using such links. Our affiliates include the following:
			</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>AvantLink</li>
				<li>Awin (Affiliate Window)</li>
				<li>CJ Affiliate by Conversant</li>
				<li>Clickbank</li>
				<li>FlexOffers</li>
				<li>iDevAffiliate</li>
				<li>MaxBounty</li>
				<li>Rakuten Affiliate Network</li>
				<li>RevenueWire</li>
				<li>ShareASale</li>
			</ul>
			<p className="mb-4">
				We are a participant in the Amazon Services LLC Associates Program, an
				affiliate advertising program designed to provide a means for us to earn
				advertising fees by linking to Amazon.com and affiliated websites.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">TESTIMONIALS DISCLAIMER</h2>
			<p className="mb-4">
				The Site may contain testimonials by users of our products and/or
				services. These testimonials reflect the real-life experiences and
				opinions of such users. However, the experiences are personal to those
				particular users, and may not necessarily be representative of all users
				of our products and/or services. We do not claim, and you should not
				assume, that all users will have the same experiences. YOUR INDIVIDUAL
				RESULTS MAY VARY.
			</p>
			<p className="mb-4">
				The testimonials on the Site are submitted in various forms such as
				text, audio and/or video, and are reviewed by us before being posted.
				They appear on the Site verbatim as given by the users, except for the
				correction of grammar or typing errors. Some testimonials may have been
				shortened for the sake of brevity where the full testimonial contained
				extraneous information not relevant to the general public.
			</p>
			<p className="mb-4">
				The views and opinions contained in the testimonials belong solely to
				the individual user and do not reflect our views and opinions. We are
				not affiliated with users who provide testimonials, and users are not
				paid or otherwise compensated for their testimonials.
			</p>
			<p className="mb-4">
				The testimonials on the Site are not intended, nor should they be
				construed, as claims that our products and/or services can be used to
				diagnose, treat, mitigate, cure, prevent, or otherwise be used for any
				disease or medical condition. No testimonials have been clinically
				proven or evaluated.
			</p>
		</main>
	)
}
